import {getEventStoppageType, isStoppageTypeWithReason} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {DEPARTMENT_BY_TYPE} from '@hconnect/common/consts'
import {enrichNodePath} from '@hconnect/common/mappers'
import {
  fetchDepartmentUpmIdByPxTrendTagSuggestion,
  fetchMainEquipmentByPxTrendTagSuggestion,
  fetchUpmStructure,
  getQueryKeyDepartmentUpmIdByPxTrendTagSuggestion,
  getQueryKeyMainEquipmentByPxTrendTagSuggestion,
  getQueryKeyUpmStructure
} from '@hconnect/common/services'
import {
  EquipmentData,
  PlantNode,
  RunningTimeDto,
  RunningTimeStoppage,
  StoppageDto as Stoppage,
  UpmEntityType
} from '@hconnect/common/types'
import {getSubNodesByUpmType} from '@hconnect/common/utils'
import {useQueryClient} from '@tanstack/react-query'

export const getInitialStoppageData = async ({
  rt,
  equipment,
  plantId,
  queryClient,
  axiosInstance,
  stoppage,
  enableMainEquipmentPrefilling = true
}: {
  rt: RunningTimeDto
  equipment: EquipmentData
  plantId: string
  queryClient: ReturnType<typeof useQueryClient>
  axiosInstance: typeof ApiClient.axiosInstance
  stoppage?: RunningTimeStoppage
  enableMainEquipmentPrefilling?: boolean
}) => {
  let suggestedMainEquipment: EquipmentData | undefined = undefined
  let suggestedDepartmentUpmId: string | undefined = undefined
  const [mainEquipmentResult, departmentUpmIdResult] = await Promise.allSettled([
    enableMainEquipmentPrefilling
      ? queryClient.fetchQuery({
          queryKey: [getQueryKeyMainEquipmentByPxTrendTagSuggestion(plantId, equipment.id)],
          queryFn: () =>
            fetchMainEquipmentByPxTrendTagSuggestion({
              plantId,
              pxTrendTag: equipment.id,
              axiosInstance: ApiClient.axiosInstance
            }),
          staleTime: 1000 * 60 * 15
        })
      : Promise.resolve(undefined),
    queryClient.fetchQuery({
      queryKey: [getQueryKeyDepartmentUpmIdByPxTrendTagSuggestion(plantId, equipment.id)],
      queryFn: () =>
        fetchDepartmentUpmIdByPxTrendTagSuggestion({
          plantId,
          pxTrendTag: equipment.id,
          axiosInstance
        }),
      staleTime: 1000 * 60 * 15
    })
  ])

  if (mainEquipmentResult.status === 'fulfilled') {
    suggestedMainEquipment = mainEquipmentResult.value
  } else {
    console.error(
      'Error fetching main equipment by px trend tag suggestion',
      mainEquipmentResult.reason
    )
  }

  let structure: PlantNode | undefined = undefined
  if (departmentUpmIdResult.status === 'fulfilled') {
    suggestedDepartmentUpmId = departmentUpmIdResult.value

    try {
      const structureData = await queryClient.fetchQuery({
        queryKey: getQueryKeyUpmStructure(plantId),
        queryFn: () =>
          fetchUpmStructure({
            plantId,
            includeEquipment: true,
            axiosInstance: ApiClient.axiosInstance
          }),
        staleTime: 1000 * 60 * 15
      })
      structure = enrichNodePath(structureData)
    } catch (error) {
      console.error('Error fetching upm structure', error)
    }
  } else {
    console.error(
      'Error fetching department upm id by px trend tag suggestion',
      departmentUpmIdResult.reason
    )
  }
  return generateStoppageDtoDefaultData({
    runningTime: rt,
    equipment,
    suggestedMainEquipment,
    suggestedDepartmentUpmId,
    stoppage,
    structure
  })
}

export const generateStoppageDtoDefaultData = ({
  runningTime,
  equipment,
  suggestedMainEquipment,
  suggestedDepartmentUpmId,
  stoppage,
  structure
}: {
  runningTime: RunningTimeDto
  equipment: EquipmentData
  suggestedMainEquipment?: EquipmentData
  suggestedDepartmentUpmId?: string
  stoppage?: RunningTimeStoppage
  structure?: PlantNode
}): Partial<Stoppage> => {
  const stoppageType = getEventStoppageType(equipment)
  const stoppageTypeData = stoppageType
    ? {
        stoppageType,
        ...(isStoppageTypeWithReason(stoppageType) && {stoppageReason: undefined})
      }
    : {}
  const stoppageStart = stoppage?.start.toISOString() ?? runningTime.beginTechnical
  const stoppageEnd = stoppage?.end?.toISOString() ?? runningTime.endTechnical

  const suggestedDepartment =
    structure && suggestedDepartmentUpmId
      ? getSubNodesByUpmType(structure, suggestedDepartmentUpmId, UpmEntityType.Department)[0]
      : undefined
  const availableCodes: string[] = DEPARTMENT_BY_TYPE[stoppageType]

  return {
    stoppageStart,
    // HCP-42934 uncomment when management decides
    // costCurrency: defaultCurrency,
    pxTrendTag: equipment.id,
    ...(stoppageEnd ? {stoppageEnd} : {}),
    pxTrendStoppage: runningTime,
    ...(suggestedMainEquipment ? {mainEquipment: suggestedMainEquipment} : {}),
    ...(suggestedDepartmentUpmId &&
    availableCodes?.some((code) => suggestedDepartment?.entityCodesPath === code)
      ? {upmDepartmentId: suggestedDepartmentUpmId}
      : {}),
    ...stoppageTypeData
  }
}
